// ----------------------------------------------------------------------
// <copyright file="Izenda.SubReport.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {action, observable, toJS} from 'mobx';
import constants from 'logic/data/Izenda.ReportDesignerConstants';
import propBuilderConstants from 'logic/data/Izenda.PropertiesBuilderConstants';
import {isNil, join, map, forEach, filter, compact, flow, set, find} from 'lodash/fp';

const CALCULATED_FIELD_CATE_NAME = 'Calculated Fields';
const subReportConstant = propBuilderConstants.SUB_REPORT;

export default class SubReport {

  ADD_MAPPING_FIELD_BUTTON = 'addMappingField';

  @observable fieldInCurrentReportPart = [];
  @observable fieldInSubReport = [];
  @observable gridData = [];
  @observable reportParts = [];
  @observable selectedReport = null;
  @observable style = constants.DRIL_DOWN_STYLE.LINK_NEW_WINDOW;
  @observable reportPartUsed = null;
  @observable reportFilter = true;
  @observable viewSettingByLink = true;
  @observable selectedReportName = null;
  @observable selectedIconValue = {
    icon: subReportConstant.DEFAULT_ICON.icon,
    value: subReportConstant.DEFAULT_ICON.value
  };

  /**
   * Read initial data from props.
   * @param {any} data Raw data.
   * @param {boolean} hideViewSetting Force viewSettingByLink true value.
   */
  @action
  readInitialData(data: any, hideViewSetting: boolean) {
    data = data || {};
    const viewSettingByLink = !isNil(data.viewSettingByLink) ? !!data.viewSettingByLink : true;
    const selectedIconValue = data.selectedIconValue;

    this.selectedReport = data.selectedReport;
    this.style = data.style || constants.DRIL_DOWN_STYLE.LINK_NEW_WINDOW;
    this.reportPartUsed = data.reportPartUsed || null;
    this.gridData = data.mappingFields && toJS(data.mappingFields);
    this.reportFilter = data.reportFilter;
    if (selectedIconValue && !isNil(selectedIconValue.icon) && !isNil(selectedIconValue.value)) {
      this.selectedIconValue = selectedIconValue;
    }
    this.viewSettingByLink = hideViewSetting ? true : viewSettingByLink;
  }

  /**
   * Read list Reports, field in current report from API
   * @param {Object} data
   */
  @action
  readReports(data) {
    this.selectedReportName = data.reportData.name;
    this.fieldInCurrentReportPart = data.fieldInCurrentReportPart;
    this.readSubReport(data);
    this.populateReportsListData();
  }

  /**
   * [populateReportsListData function is to set up data when the report list have no category will store in group
   * named 'Uncategorized'
   */
  @action
  populateReportsListData() {
    forEach(report => {
      if (!report.name && report.id === subReportConstant.UNDEFINED) {
        report.name = subReportConstant.UNCATEGORIZED;
      }
    }, this.reports);
  }

  /**
   * Read list fields in sub report from API
   * @param {Object} data
   */
  @action
  readSubReport(data) {

    this.fieldInSubReport = map(field => {
      const valueId = join('_', compact([field.relationshipId, field.querySourceFieldId]));
      let fieldNameInSubReport = String.format('[{0}]', field.sourceFieldName);

      // if this field is Calculated field don't need to add source data object name
      if (!(field.isCalculated && field.querySourceType === CALCULATED_FIELD_CATE_NAME)) {
        fieldNameInSubReport = String.format('[{0}].{1}', field.sourceDataObjectName, fieldNameInSubReport);

        if (!field.relationshipId && field.connectionName && field.querySourceCategoryName) {
          fieldNameInSubReport = String.format(
            '[{0}].[{1}].{2}',
            field.connectionName,
            field.querySourceCategoryName,
            fieldNameInSubReport
          );
        }
      }
      return flow(
        set('id', valueId),
        set('fieldNameInSubReport', fieldNameInSubReport)
      )(field);
    }, data.fieldInSubReport || []);

    this.reportParts = data.reportData.reportPart || [];
  }

  /**
   * Update selected report
   * @param {Object} report
   */
  @action
  updateSelectedReport(report) {
    this.selectedReport = report.id || null;
    this.selectedReportName = report.name || null;
    // Clear mappings
    this.gridData = [];
  }

  /**
   * Update Style
   * @param {String} value
   */
  @action
  updateStyle(value) {
    this.style = value;
  }

  /**
   * Update Report Part
   * @param {String} reportParts
   */
  updateReportPart(reportParts) {
    let reportPartUsed = join(',', map('id', reportParts || []));
    if (reportParts && reportParts.indexOf(subReportConstant.ALL_OPTION) > -1) {
      reportPartUsed = 'All';
    }
    this.reportPartUsed = reportPartUsed;
  }

  /**
   * Update Report Filter
   * @param {String} value
   */
  @action
  updateReportFilter(value) {
    this.reportFilter = value;
  }

  /**
   * Add mapping field
   */
  @action
  addMappingField() {
    this.gridData.push({
      fieldNameAlias: '',
      fieldInSubReport: '',
      relationshipInSubReport: '',
      fieldNameInSubReport: ''
    });
  }

  /**
   * Remove a mapping field
   * @param {number} index
   */
  removeMappingField(index) {
    index > -1 && this.gridData.splice(index, 1);
  }

  /**
   * Remove empty field mapping's row
   */
  @action
  removeUnusedMappingField() {
    // Filter empty row
    this.gridData = filter(item => {
      return item.fieldNameAlias || item.fieldInSubReport || item.relationshipInSubReport;
    }, this.gridData);
  }

  /**
   * Toggle the state of view setting link
   */
  @action
  updateViewSettingByLink() {
    this.viewSettingByLink = !this.viewSettingByLink;
  }

  /**
   * [updateSelectedViewSettingIcon]
   * @param  {[type]} value [description]
   */
  @action
  updateSelectedViewSettingIcon(value) {
    const selectedIcon = find({value: value}, subReportConstant.ICON_TYPES);
    if (selectedIcon) {
      this.selectedIconValue = {
        icon: selectedIcon.icon,
        value: selectedIcon.value
      };
    }
  }

  validateDuplicate() {
    const duplicateMap = {};
    const duplicateIndex = [];

    const gridDataRaw = toJS(this.gridData || []);
    forEach((item, index) => {
      const key = join('_', [item.fieldNameAlias, item.fieldNameInSubReport]);

      if (duplicateMap[key]) {
        duplicateIndex.push(index);
      } else {
        duplicateMap[key] = true;
      }
    }, gridDataRaw);

    return map(item => {
      return {
        key: subReportConstant.SUB_REPORT_GRID.VALIDATOR_GROUP + item,
        messages: [subReportConstant.SUB_REPORT_GRID.DUPLICATE_ERROR_MESSAGE]
      };
    }, duplicateIndex);
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.SubReport.js